import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Container, Box, Grid2 as Grid, Paper, CircularProgress } from '@mui/material';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import API from '@utils/axios';
import LangSelect from '@components/LangSelect';
import PasswordField from '@components/PasswordField';
import { isPasswordValid } from '@utils/passwordValidation';

function ChangePasswordPage() {
  const { t, i18n } = useTranslation();
  const { doLogout, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Vérifier si les mots de passe correspondent
    if (formData.password !== formData.password_confirmation) {
      showMessage(t('Passwords do not match'), 'error');
      return;
    }

    // Vérifier si le mot de passe respecte toutes les règles
    if (!isPasswordValid(formData.password)) {
      showMessage(t('Password does not meet the requirements'), 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await API.put('/account', formData);
      showMessage(t(response.data.message), 'success');
      await refreshAuth(); // Sinon on retombe à nouveau sur la page de changement de mot de passe
      navigate('/');
    } catch (err) {
      showMessage(
        err.response?.data?.message ? t(err.response.data.message) : err.message,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ width: '100%', p: 4, mt: { xs: 2, sm: 6, md: 12 } }}>
        <img src="/logo.webp" alt="Logo" style={{ width: '100%', height: 'auto', marginBottom: 16 }} />

        <Grid container>
          <Grid size="grow" xs>
            <Typography component="h1" variant="h2">
              {t("Password")}
            </Typography>
          </Grid>
          <Grid>
            <LangSelect
              variant="standard"
              value={i18n.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Language' }}
            />
          </Grid>
        </Grid>

        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {t("For security reasons, you must change your password.") + ' ' + t("Please enter your new password below.")}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <Box sx={{ mt: 2, mb: 2 }}>
            <PasswordField
              field={{
                value: formData.password,
                onChange: (e) => handleChange('password')(e),
                name: 'password',
              }}
              label={t("New Password")}
            />
          </Box>
          <Box sx={{ mt: 2, mb: 2 }}>
            <PasswordField
              field={{
                value: formData.password_confirmation,
                onChange: (e) => handleChange('password_confirmation')(e),
                name: 'password_confirmation',
              }}
              label={t("Confirm New Password")}
              showRules={false}
            />
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('Validate')
            )}
          </Button>
          <Grid container justifyContent="center">
            <Grid>
              <Button fullWidth onClick={doLogout}>
                {t('Logout')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default ChangePasswordPage; 